import { Injectable } from '@angular/core';
import { EntryErrorModel, InvoiceModel } from '@data-layer/models/entry-summaries';
import { ManagementIsfModel } from '@data-layer/models/management';
import { MaskPipe } from 'ngx-mask';
import { HTS_MASK } from '@helpers/custom-validator';
import { enumIsfShipmentCode } from '@data-layer/enums/isf';
import { IsfModel } from '@data-layer/models/isf';
import { IsfHelper } from '@helpers/isf.helper';
import { flatten } from 'lodash-es';
import {
  BUYER_MISSING,
  CONSIGNEE_MISSING,
  SHIP_TO_MISSING,
  SELLER_MISSING,
  MISSING_ADDRESS,
  MISSING_CANADA_STATE,
  MISSING_CITY,
  MISSING_POSTAL_CODE,
  MISSING_STATE,
  MISSING_COMPANY_NAME,
  INVOICE_MANUFACTURER_MISSING,
  BILL_CARRIER_MISSING,
  BILL_NUMBER_MISSING,
} from '@constants/entity-validation-messages';
import { ShipmentModel, ValidationMessageModel } from '@app/data-layer/models/common';
import { EntityModel } from '@app/data-layer/models/entities';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';

@Injectable({
  providedIn: 'root',
})
export class IsfValidationService {
  constructor(private maskPipe: MaskPipe) {}

  public getErrorParams(message: EntryErrorModel, isf: IsfModel | ManagementIsfModel) {
    switch (message.errorCode) {
      case 'BILL_NOT_UNIQUE':
        return {
          carrier: isf.shipment.code === enumIsfShipmentCode.HOUSE ? isf.shipment.houseBill.carrier : isf.shipment.masterBill.carrier,
          number: isf.shipment.code === enumIsfShipmentCode.HOUSE ? isf.shipment.houseBill.number : isf.shipment.masterBill.number,
        };
        break;

      case 'CARRIER_NOT_FOUND':
        return {
          carrier: isf.shipment.code === enumIsfShipmentCode.HOUSE ? isf.shipment.houseBill.carrier : isf.shipment.masterBill.carrier,
        };
        break;

      case 'HTS_CODE_NOT_ON_FILE':
        const invoices = IsfHelper.getIsfInvoicesList(isf);
        const lines = flatten(invoices.map((invoice) => invoice?.lines));

        const htsNumber = lines.length ? lines[0].htsNumber : '';

        return {
          htsNumber: htsNumber ? this.maskPipe.transform(htsNumber, HTS_MASK) : '',
        };
        break;

      default:
        break;
    }
    return {};
  }

  public isQuickFixConsigneeDetailsInvalid(shipment: ShipmentModel) {
    const shValidationMessages = shipment?.validationMessages || [];
    const cnAddressValidationMessages: ValidationMessageModel[] = Object.keys(shipment?.consignee?.address?.validationMessages || []).map(
      (key) => shipment?.consignee?.address?.validationMessages[key],
    );

    return !![...shValidationMessages, ...cnAddressValidationMessages].find((error) => {
      switch (error?.text) {
        case CONSIGNEE_MISSING:
        case MISSING_COMPANY_NAME:
        case MISSING_ADDRESS:
        case MISSING_CITY:
        case MISSING_STATE:
        case MISSING_CANADA_STATE:
        case MISSING_POSTAL_CODE:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixBuyerDetailsInvalid(shipment: ShipmentModel) {
    const shValidationMessages = shipment?.validationMessages || [];
    const byAddressValidationMessages: ValidationMessageModel[] = Object.keys(shipment?.buyer?.address?.validationMessages || []).map(
      (key) => shipment?.buyer?.address?.validationMessages[key],
    );

    return !![...shValidationMessages, ...byAddressValidationMessages].find((error) => {
      switch (error?.text) {
        case BUYER_MISSING:
        case MISSING_COMPANY_NAME:
        case MISSING_ADDRESS:
        case MISSING_CITY:
        case MISSING_STATE:
        case MISSING_CANADA_STATE:
        case MISSING_POSTAL_CODE:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixShipToDetailsInvalid(shipment: ShipmentModel) {
    const shValidationMessages = shipment?.validationMessages || [];
    const stAddressValidationMessages: ValidationMessageModel[] = Object.keys(shipment?.shipTo?.address?.validationMessages || []).map(
      (key) => shipment?.shipTo?.address?.validationMessages[key],
    );
    return !![...shValidationMessages, ...stAddressValidationMessages].find((error) => {
      switch (error.text) {
        case SHIP_TO_MISSING:
        case MISSING_COMPANY_NAME:
        case MISSING_ADDRESS:
        case MISSING_CITY:
        case MISSING_STATE:
        case MISSING_CANADA_STATE:
        case MISSING_POSTAL_CODE:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixSupplierDetailsInvalid(shipment: ShipmentModel) {
    const shValidationMessages = shipment?.validationMessages || [];
    const spAddressValidationMessages: ValidationMessageModel[] = Object.keys(shipment?.seller?.address?.validationMessages || []).map(
      (key) => shipment?.seller?.address?.validationMessages[key],
    );
    return !![...shValidationMessages, ...spAddressValidationMessages].find((error) => {
      switch (error.text) {
        case SELLER_MISSING:
        case MISSING_COMPANY_NAME:
        case MISSING_ADDRESS:
        case MISSING_CITY:
        case MISSING_STATE:
        case MISSING_CANADA_STATE:
        case MISSING_POSTAL_CODE:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixConsolidatorDetailsInvalid(shipment: ShipmentModel) {
    const csValidationMessages = shipment?.consolidator?.validationMessages || [];
    const consolidator = shipment?.consolidator;
    const isCsEntityValid =
      !!consolidator &&
      (consolidator?.validationMessages?.length === 0 || !consolidator?.validationMessages) &&
      this.isValidEntityAddress(consolidator);

    const isEntityMatchingValidationMessages = !![...csValidationMessages].find((error) => {
      switch (error.text) {
        case MISSING_COMPANY_NAME:
          return true;
        default:
          return false;
      }
    });

    return !isCsEntityValid || isEntityMatchingValidationMessages;
  }

  public isQuickFixCslDetailsInvalid(shipment: ShipmentModel) {
    const cslValidationMessages = shipment?.stuffingLocation?.validationMessages || [];
    const containerStuffingLocation = shipment?.stuffingLocation;
    const isCslEntityValid =
      !!containerStuffingLocation &&
      (containerStuffingLocation?.validationMessages?.length === 0 || !containerStuffingLocation?.validationMessages) &&
      this.isValidEntityAddress(containerStuffingLocation);

    const isEntityMatchingValidationMessages = !![...cslValidationMessages].find((error) => {
      switch (error.text) {
        case MISSING_COMPANY_NAME:
          return true;
        default:
          return false;
      }
    });

    return !isCslEntityValid || isEntityMatchingValidationMessages;
  }

  public isQuickFixManufacturerDetailsInvalid(invoice: InvoiceModel) {
    const invoiceValidationMessages = invoice?.validationMessages || [];

    return !!invoiceValidationMessages.find((error) => {
      switch (error.text) {
        case INVOICE_MANUFACTURER_MISSING:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixBlDetailsInvalid(shipment: ShipmentModel) {
    const shValidationMessages = shipment?.validationMessages || [];

    return !!shValidationMessages.find((error) => {
      switch (error.text) {
        case BILL_CARRIER_MISSING:
        case BILL_NUMBER_MISSING:
          return true;
        default:
          return false;
      }
    });
  }

  private isValidEntityAddress(entity: EntityModel): boolean {
    const missingAddressKeys = ['country', 'city', 'line1'];
    const isFilledAddress = missingAddressKeys.every((item) => entity?.address?.[item]);

    return EntityValidationHelper.isValidEntityAddress(entity?.address) && isFilledAddress;
  }
}
